import { useEffect } from 'react'
import {
  useForm,
  FormProvider,
  FieldValues,
  DefaultValues
} from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Button, H5, Modal } from '@pay-com/components'
import { ControlledFields } from 'components/ControlledFields'
import { TooltipFieldProvider } from 'components/TooltipFieldWrapper'
import { IFormConfig } from 'types'

type IndividualModalProps<T extends FieldValues> = {
  isOpen: boolean
  title: string
  subTitle?: string
  submitLabel: string
  config: IFormConfig<T>
  defaultValues?: DefaultValues<T>
  isSaving?: boolean
  onConfirm: (data: T) => void
  onDismiss: () => void
}

export const IndividualModal = <T extends FieldValues>({
  isOpen,
  title,
  subTitle,
  submitLabel,
  config,
  defaultValues,
  isSaving = false,
  onConfirm,
  onDismiss
}: IndividualModalProps<T>) => {
  const methods = useForm<T>({
    resolver: zodResolver(config.schema),
    defaultValues,
    mode: 'onBlur',
    reValidateMode: 'onChange'
  })

  useEffect(() => {
    if (isOpen) {
      methods.reset(defaultValues)
    }
  }, [isOpen, defaultValues])

  const handleSubmit = methods.handleSubmit(data => onConfirm(data))

  return (
    <Modal.Root open={isOpen} onOpenChange={open => !open && onDismiss()}>
      <Modal className="flex h-[40rem] max-h-[90vh] flex-col">
        <Modal.Header>
          <H5 className="!text-[26px]">{title}</H5>
        </Modal.Header>
        <Modal.Subtitle className="!mt-0 mb-4">{subTitle}</Modal.Subtitle>
        <Modal.Content className="modal-scrollbar flex-1 overflow-y-auto">
          <FormProvider {...methods}>
            <TooltipFieldProvider>
              <div className="flex flex-col gap-4">
                <ControlledFields fields={config.fields} />
              </div>
            </TooltipFieldProvider>
          </FormProvider>
        </Modal.Content>
        <Modal.Actions className="mt-auto pt-4">
          <Button
            variant="text"
            type="button"
            onClick={onDismiss}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            isLoading={isSaving}
            disabled={isSaving}
          >
            {submitLabel}
          </Button>
        </Modal.Actions>
      </Modal>
    </Modal.Root>
  )
}
